import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'domaingrc.sidenav.collapsed';

/**
 * Collapsed/expanded state for the left navigation rail.
 *
 * Lives in a service rather than the sidenav component because the rail's
 * WIDTH is applied to the `<mat-sidenav>` element owned by AppComponent, while
 * the toggle lives inside SidenavComponent — both need to read the same state.
 *
 * The choice is persisted so a collapsed rail survives a reload.
 */
@Injectable({ providedIn: 'root' })
export class SidenavUiService {
  private readonly _collapsed = signal(this.readPersisted());
  readonly collapsed = this._collapsed.asReadonly();

  toggle(): void {
    this.setCollapsed(!this._collapsed());
  }

  private setCollapsed(collapsed: boolean): void {
    this._collapsed.set(collapsed);
    this.persist(collapsed);
  }

  // SSR-safe: this app server-renders (main.server.ts), where localStorage
  // does not exist. Default to expanded there.
  private readPersisted(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      // Private-mode / blocked storage — fall back to expanded.
      return false;
    }
  }

  private persist(collapsed: boolean): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // Persistence is a convenience; ignore quota/permission failures.
    }
  }
}
